'use client';

import { CardHeader, createMultiStyleConfigHelpers, StyleProps } from '@chakra-ui/react';
import { cardAnatomy } from '@chakra-ui/anatomy';

import Icon from '@/components/atomic/v1/icon/Icon';

import { CardBody } from './CardBody';
import { CommonCardProps } from './cardTheme';
import { CardContainer } from './CardContainer';

const { definePartsStyle } = createMultiStyleConfigHelpers(cardAnatomy.keys);

export const myomersDefaultVariant = definePartsStyle({
  container: {
    p: { base: 4, md: 6 }
  }
});

/**
 *
 * Card myOMERS component
 *
 * If you would like to use a card with a ColorStrip + Border, use the CardOutline component instead.
 *
 * This component only has an option to add an icon. To add an image/video, use the `MediaCard` component, which is a slightly specialized card component. Either an icon or image/video can be added on a card, but not both.
 *
 * To add an icon, pass in the `icon` prop and, optionally, the `iconSize` prop.
 *
 * The optional color strip on the `Card` component has two placements: top (the default) and left - depending on the layout.
 *
 * The `Card` has two layout options: vertical (the default) and horizontal. When the layout is set to horizontal, it only takes effect above the medium breakpoint set in the theme. Also, setting the `layout` prop to horizontal places the color strip on the left.
 *
 * The `cardOutline` prop specifies whether the card has a drop shadow (the default look) or has a border instead when the value is set to `border`. This also applies to the `MediaCard`.
 *
 * The link and two buttons that appear at the bottom of the card can also be optionally added.
 */

const Card = ({
  layout = 'vertical',
  colorStripColor,
  cardOutline = 'drop-shadow',
  iconSize = 48,
  btnColor = 'blue',
  icon,
  eyebrowText,
  headerText,
  bodyContent,
  primaryBtnText,
  primaryBtnUrl,
  secondaryBtnText,
  secondaryBtnUrl,
  footerLinkText,
  footerLinkUrl,
  bodyPadding = 0,
  height
}: CommonCardProps) => {
  const hasIcon = !!icon;

  const withColorStripStyles = colorStripColor
    ? ({
        borderLeftWidth: layout === 'horizontal' ? 8 : 0,
        borderTopWidth: layout === 'vertical' ? 8 : 0,
        borderColor: colorStripColor ?? 'transparent',
        borderStyle: colorStripColor ? 'solid' : 'none'
      } satisfies StyleProps)
    : {};

  return (
    <CardContainer
      variant="myomersDefault"
      layout={layout}
      cardOutline={cardOutline}
      {...withColorStripStyles}
      {...(height ? { height } : {})}
    >
      {hasIcon && (
        <CardHeader position="relative">
          <Icon name={icon} size={iconSize} color="icon/default" />
        </CardHeader>
      )}

      <CardBody
        bodyContent={bodyContent}
        headerText={headerText}
        eyebrowText={eyebrowText}
        footerLinkText={footerLinkText}
        footerLinkUrl={footerLinkUrl}
        primaryBtnText={primaryBtnText}
        primaryBtnUrl={primaryBtnUrl}
        secondaryBtnText={secondaryBtnText}
        secondaryBtnUrl={secondaryBtnUrl}
        btnColor={btnColor}
        padding={bodyPadding}
      />
    </CardContainer>
  );
};

export { Card };
